import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { useCallback } from "react";

import {
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  transientOptions,
  useSearch,
} from "@skilitics/designer-ui-components";
import { FormattedMessage, useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { createFolderFilter } from "../filters";
import { useLandingPageSettings } from "../LandingPageSettingsContext";
import { getFolderNamesFromFilter, sliceFolderFilter } from "../shared";
import type { Area } from "../types";
import { useActiveArea } from "../useActiveArea";
import { FilterSet } from "./FilterSet";

const Container = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  width: 100%;

  padding: 0 var(--page-padding-inline);

  min-width: var(--min-width);
  overflow: hidden;
  user-select: none;
`;

const ContentContainer = styled.div<{ $area: Area }>`
  display: flex;
  align-items: center;
  gap: 20px;

  width: 100%;
  height: 48px;

  font-size: var(--font-size-small);
  font-weight: ${FONT_WEIGHT_MEDIUM};

  background-color: var(--background-color);
  border-bottom: 1px solid ${({ theme }) => theme.outlineSoft};

  opacity: ${({ $area }) => ($area === "modules" ? 1 : 0)};
  pointer-events: ${({ $area }) => ($area === "modules" ? "auto" : "none")};

  transition: opacity 300ms ease-in-out;
`;

// Path shrinks and scrolls; the root and the right-side elements keep their size.
const PathContainer = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 0 1 auto;

  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  & span {
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};
  }
`;

const RootContainer = styled.span`
  position: relative;

  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;

  background-color: var(--background-color);
  cursor: pointer;
  z-index: 1;

  // Fade the scrolling path as it slides under the pinned root.
  &::after {
    content: "";
    position: absolute;
    left: 100%;
    top: 0;

    width: 32px;
    height: 100%;

    background: linear-gradient(to right, var(--background-color), transparent);
    pointer-events: none;
  }
`;

const Caret = styled(Icon, transientOptions)<{ $active: boolean }>`
  transform: ${({ $active }) => ($active ? "rotateY(0deg)" : "rotateY(180deg)")};

  transition: transform 300ms ease-in-out;
  svg {
    width: 12px;
  }

  cursor: ${({ $active }) => ($active ? "pointer" : "default")};
`;

// rtl anchors the scroll position to the tail, so the deepest folder stays visible
// as the path grows — no measurement needed. Inner content renders ltr.
const Scroller = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  overflow-x: auto;
  direction: rtl;
  white-space: nowrap;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PathElements = styled.span`
  direction: ltr;
  display: inline-flex;
  align-items: center;

  span::before {
    content: "/";
    padding: 0 15px;
    color: ${({ theme }) => theme.generalTextIconVariant};
  }

  span:last-of-type {
    cursor: default;
  }
`;

const ResultsContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;

  white-space: nowrap;

  background-color: var(--background-color);
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  flex: 0 0 auto;

  margin-inline-start: auto;

  background-color: var(--background-color);
`;

const Separator = styled.div`
  height: 18px;
  width: 1px;

  background: ${({ theme }) => theme.surfaceBack_1};

  margin: 0 20px;
`;

export const Breadcrumbs = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { results } = useSearch();
  const { addFilterSet, getFilterSet, hasFilterSet, removeFilterSet } = useLandingPageSettings();
  const area = useActiveArea();

  const folderFilter = getFilterSet("folder")?.metadata as string;

  const resultCount = results?.length ?? 0;

  const handleClick = useCallback(() => removeFilterSet("folder"), [removeFilterSet]);

  const createHandleClick = useCallback(
    (index: number) => () => {
      const filter = sliceFolderFilter(folderFilter, index);

      addFilterSet({ id: "folder", filters: [createFolderFilter(filter)], operator: "and", metadata: filter });
    },
    [addFilterSet, folderFilter],
  );

  return (
    <Container>
      <ContentContainer $area={area}>
        <PathContainer $active={!!folderFilter} data-test="path-container">
          <RootContainer onClick={handleClick}>
            <Caret $active={!!folderFilter} faPrefix="fas" icon="caret-left" size={16} />
            {formatMessage("projects")}
          </RootContainer>
          <Scroller data-test="scrolling-container">
            <PathElements>
              <AnimatePresence>
                {getFolderNamesFromFilter(folderFilter).map((name, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={createHandleClick(index)}
                    style={{ cursor: "pointer" }}
                  >
                    {name}
                  </motion.span>
                ))}
              </AnimatePresence>
            </PathElements>
          </Scroller>
        </PathContainer>
        {results && (
          <ResultsContainer data-test="results-container">
            <Separator />
            <FormattedMessage messages={formatStrings.result} values={{ resultCount }} />
          </ResultsContainer>
        )}
        <FiltersContainer data-test="filters-container">
          {hasFilterSet("tags") && <FilterSet id="tags" icon="tag" />}
        </FiltersContainer>
      </ContentContainer>
    </Container>
  );
};

const formatStrings = {
  projects: {
    en: "Projects",
  },
  result: {
    en: "Showing {resultCount, plural, =1 {# result} other {# results}} matching your criteria...",
  },
} satisfies FormatMessages;
